import React, { useState } from 'react';
import { MessageSquare, Mail, Bell, Clock, Upload, Image, Smile } from 'lucide-react';
import { useUserStore } from '../../../stores/userStore';
import toast from 'react-hot-toast';

export default function SupportPreferences() {
  const { isPro, isAdmin } = useUserStore(state => ({ 
    isPro: state.isPro, 
    isAdmin: state.isAdmin 
  }));
  
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    supportReminders: true,
    chatTranscripts: true,
    proactiveSupport: false,
    aiAssistance: true,
    fileSharing: true
  });

  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, [key]: !prev[key] };
      toast.success('Preferências de suporte atualizadas!');
      return newPrefs;
    });
  };

  // Admin users also have access to Pro support
  const hasProSupport = isPro() || isAdmin();

  return (
    <div className="bg-white p-6 rounded-lg">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Preferências de Suporte
      </h2>

      {hasProSupport ? (
        <div className="space-y-6">
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 mb-6">
            <div className="flex items-start space-x-3">
              <MessageSquare className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-indigo-900">Suporte Pro Ativo</h3>
                <p className="text-indigo-700 text-sm mt-1">
                  {isAdmin() 
                    ? "Como administrador, você tem acesso ao suporte por chat ao vivo com IA e tempo de resposta prioritário."
                    : "Como assinante Pro, você tem acesso ao suporte por chat ao vivo com IA e tempo de resposta prioritário."}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Notificações por Email</h3>
                <p className="text-sm text-gray-600">Receba atualizações sobre seus tickets de suporte</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Lembretes de Suporte</h3>
                <p className="text-sm text-gray-600">Receba lembretes sobre tickets abertos</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.supportReminders}
                  onChange={() => handleToggle('supportReminders')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Transcrições de Chat</h3>
                <p className="text-sm text-gray-600">Receba transcrições das suas conversas de suporte</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.chatTranscripts}
                  onChange={() => handleToggle('chatTranscripts')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900">Assistência com IA</h3>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full">Pro</span>
                <p className="text-sm text-gray-600">Receba respostas inteligentes baseadas em IA</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.aiAssistance}
                  onChange={() => handleToggle('aiAssistance')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900">Compartilhamento de Arquivos</h3>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full">Pro</span>
                <p className="text-sm text-gray-600">Envie arquivos e imagens no chat de suporte</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.fileSharing}
                  onChange={() => handleToggle('fileSharing')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Suporte Proativo</h3>
                <p className="text-sm text-gray-600">Permita que nossa equipe entre em contato proativamente</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.proactiveSupport}
                  onChange={() => handleToggle('proactiveSupport')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-medium text-gray-900 mb-3">Recursos de Chat Pro</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <Upload className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Envio de Arquivos</p>
                <p className="text-xs text-gray-600">Compartilhe documentos</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <Image className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Envio de Imagens</p>
                <p className="text-xs text-gray-600">Compartilhe screenshots</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <Smile className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Emojis</p>
                <p className="text-xs text-gray-600">Expressões visuais</p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-medium text-gray-900 mb-3">Horários de Disponibilidade</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horário Preferido
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="anytime">Qualquer horário</option>
                  <option value="morning">Manhã (8h - 12h)</option>
                  <option value="afternoon">Tarde (12h - 18h)</option>
                  <option value="evening">Noite (18h - 22h)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dias Preferidos
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="weekdays">Dias úteis</option>
                  <option value="weekends">Fins de semana</option>
                  <option value="anyday">Qualquer dia</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-lg text-center">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Suporte Padrão
            </h3>
            <p className="text-gray-600 mb-6">
              Você está utilizando o suporte padrão. Faça upgrade para o plano Pro para acessar recursos avançados de suporte.
            </p>
            <button
              onClick={() => window.location.href = '/dashboard/precos'}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Fazer Upgrade para Pro
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <Mail className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Suporte por Email</h3>
                <p className="text-sm text-gray-600">
                  Tempo de resposta: 24-48 horas úteis
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Notificações Básicas</h3>
                <p className="text-sm text-gray-600">
                  Receba atualizações sobre seus tickets de suporte
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <Clock className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Horário de Atendimento</h3>
                <p className="text-sm text-gray-600">
                  Segunda a sexta, das 9h às 18h
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}